import { NextResponse } from 'next/server'
import { isIPBypassed } from '../IP/isIPBypassed'
import { normalizeForwardedClientIp } from '../IP/normalizeForwardedClientIp'
import type { AppMaintenanceStatus, MaintenanceStatusApiResponse } from './types'

const CACHE_TTL_MS = 30_000
let cachedPayload: MaintenanceStatusApiResponse | null = null
let cacheTimestamp = 0

/**
 * Fetch maintenance payload via the provided maintenanceStatusUrl (Edge-safe fetch).
 * Prefer `currentAppStatus` from the response so middleware matches the same logic as the API
 * (`appName` + `apps[appName]` on the server).
 */
const fetchMaintenancePayload = async (baseUrl: string, maintenanceStatusUrl: string): Promise<MaintenanceStatusApiResponse> => {
    const now = Date.now()
    if (cachedPayload && now - cacheTimestamp < CACHE_TTL_MS) {
        return cachedPayload
    }

    const response = await fetch(new URL(maintenanceStatusUrl, baseUrl))
    if (!response.ok) {
        return { apps: {}, bypassIPs: [], appName: '', currentAppStatus: null }
    }

    const payload = (await response.json()) as MaintenanceStatusApiResponse
    cachedPayload = payload
    cacheTimestamp = now
    return payload
}

/**
 * Resolve the app status from the payload.
 * If the appName matches the appName in the payload, return the currentAppStatus.
 * Otherwise, return the app status from the apps object.
 */
const resolveAppStatus = (
    appName: string,
    payload: MaintenanceStatusApiResponse,
): AppMaintenanceStatus | undefined => {
    if (payload.appName === appName && payload.currentAppStatus != null) {
        return payload.currentAppStatus
    }
    return payload.apps[appName]
}

const DEFAULT_MAINTENANCE_PATH = '/maintenance'
const DEFAULT_MAINTENANCE_STATUS_URL = '/api/maintenance-status'

export type CheckMaintenanceOptions = {
    maintenancePath?: string
    maintenanceStatusUrl?: string
    /**
     * Base URL for the maintenance status fetch (scheme + host [+ port], no path). Overrides `request.url` origin.
     * Set env `MAINTENANCE_FETCH_ORIGIN` in middleware when the request origin is wrong inside the pod (e.g. 0.0.0.0).
     */
    maintenanceFetchOrigin?: string
}

/** Normalize the maintenance path to always start with a slash. */
const normalizeMaintenancePath = (path: string): string =>
    path.startsWith('/') ? path : `/${path}`

/**
 * Middleware helper that checks if the current app is in maintenance mode.
 * Returns a redirect response to the configured maintenance path if active, or null to continue normally.
 *
 * Requires:
 * - APP_NAME env var (e.g. admin) matching keys under `apps` and Azure keys maintenance/{appName}
 * - GET /api/maintenance-status returning apps, bypassIPs, appName, and currentAppStatus for this deployment
 *
 * @param request - Incoming middleware request (Fetch API Request; NextRequest is compatible)
 * @param options - Optional `maintenancePath` (default `/maintenance`)
 * @returns Response redirect to the maintenance path, or null if no maintenance is active
 */
export const checkMaintenance = async (
    request: Request,
    options?: CheckMaintenanceOptions,
): Promise<Response | null> => {
    const maintenancePath = normalizeMaintenancePath(options?.maintenancePath ?? DEFAULT_MAINTENANCE_PATH)
    const requestUrl = new URL(request.url)
    const { pathname } = requestUrl

    if (pathname === maintenancePath || pathname.startsWith('/api') || pathname.startsWith('/_next')) {
        return null
    }

    const appName = process.env.APP_NAME

    if (!appName) {
        return null
    }

    try {
        const fetchOrigin = (options?.maintenanceFetchOrigin?.trim() || requestUrl.origin).replace(/\/$/, '')
        const payload = await fetchMaintenancePayload(
            fetchOrigin,
            options?.maintenanceStatusUrl ?? DEFAULT_MAINTENANCE_STATUS_URL,
        )
        const appStatus = resolveAppStatus(appName, payload)

        if (!appStatus?.enabled) {
            return null
        }

        const rawClientIP =
            request.headers.get('x-forwarded-for')?.split(',')[0]?.trim() ||
            request.headers.get('x-real-ip')?.trim() ||
            ''
        const clientIP = normalizeForwardedClientIp(rawClientIP)

        if (clientIP && payload.bypassIPs.length > 0 && isIPBypassed(clientIP, payload.bypassIPs)) {
            const response = NextResponse.next()
            response.headers.set('X-Maintenance-Bypass', 'true')
            return response
        }

        return NextResponse.redirect(new URL(maintenancePath, request.url))
    } catch (error) {
        return null
    }
}

/** Reset the cache (primarily for testing). */
export const resetCheckMaintenanceCache = (): void => {
    cachedPayload = null
    cacheTimestamp = 0
}
